/**
 * useAnimatedNumber — Hook that animates a number from 0 to target value.
 * Uses requestAnimationFrame for smooth 60fps animation.
 * Returns the current interpolated value.
 */

import { useEffect, useRef, useState } from "react";

/**
 * Easing function — ease-out cubic for natural deceleration.
 */
function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

/**
 * Animate a number from 0 (or previous value) to the target.
 * @param target - Final value to reach
 * @param duration - Animation duration in ms (0 = no animation)
 */
export function useAnimatedNumber(
  target: number,
  duration: number = 1200,
): number {
  const [current, setCurrent] = useState(0);
  const prevTarget = useRef(0);
  const frameRef = useRef<number>(0);

  useEffect(() => {
    if (duration <= 0) {
      setCurrent(target);
      prevTarget.current = target;
      return;
    }

    const from = prevTarget.current;
    const to = target;
    const diff = to - from;

    if (Math.abs(diff) < 0.01) {
      setCurrent(to);
      prevTarget.current = to;
      return;
    }

    const startTime = performance.now();

    function animate(now: number) {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = easeOutCubic(progress);

      setCurrent(from + diff * eased);

      if (progress < 1) {
        frameRef.current = requestAnimationFrame(animate);
      } else {
        setCurrent(to);
        prevTarget.current = to;
      }
    }

    frameRef.current = requestAnimationFrame(animate);

    return () => {
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }
    };
  }, [target, duration]);

  return current;
}
